import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axiosInstance from '../../../constants/axiosInstance';
import { BatchConfigRow } from '../types/BatchConfigType';

interface DeleteBatchConfigPayload {
  batchId: string;
}
interface ResponseType {
  message: string;
}

const deleteBatchConfig = async (payload: DeleteBatchConfigPayload) => {
  const response = await axiosInstance().post(`/batch/deleteBatch`, payload);
  return response.data;
};

export const useDeleteBatchConfig = (handleClose: () => void, data: BatchConfigRow) => {
  const queryClient = useQueryClient();
  const { batchId } = data;

  const { mutate } = useMutation({
    mutationFn: deleteBatchConfig,
    onSuccess: (response: ResponseType) => {
      toast.success(response?.message || 'Batch Configuration deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['batchConfigs'] });
      handleClose();
    },
    onError: (error: ResponseType) => {
      toast.error(error?.message || 'Failed to delete Batch Configuration');
    },
  });

  const handleDelete = useCallback(() => {
    if (batchId) {
      mutate({ batchId });
    } else {
      toast.error('Batch Configuration ID is missing');
    }
  }, [mutate, batchId]);

  const handleCancel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const actions = useMemo(
    () => [
      {
        text: 'Cancel',
        handler: handleCancel,
        fullWidth: false,
        color: '#E0E0E0',
        textColor: '#000',
      },
      {
        text: 'Delete',
        handler: handleDelete,
        fullWidth: false,
        color: '#FF0000',
        textColor: '#FFF',
      },
    ],
    [handleCancel, handleDelete]
  );

  return {
    handleDelete,
    handleCancel,
    actions,
  };
};
